import { useEffect, useRef, useState } from "react";

/**
 * Reveal — scroll-triggered entrance via IntersectionObserver.
 * Content is always visible by default; .reveal-play class is added when in view.
 * Hard fallback at 1.5s ensures content animates in even if IO fails.
 */
const REVEAL_VARIANT_CLASS = {
  scale: "reveal reveal-scale",
  mask: "reveal reveal-mask",
  "mask-left": "reveal reveal-mask-left",
  line: "reveal reveal-line",
};

export const Reveal = ({
  children,
  delay = 0,
  variant = "fade",
  as: Tag = "div",
  className = "",
  ...rest
}) => {
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;

    const reduceMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setPlayed(true);
      return;
    }

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setPlayed(true);
    };

    const inView = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh && r.bottom > 0;
    };

    if (inView()) trigger();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trigger();
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);

    const hardTimer = window.setTimeout(trigger, 1500);

    return () => {
      io.disconnect();
      window.clearTimeout(hardTimer);
    };
  }, [played]);

  const variantClass = REVEAL_VARIANT_CLASS[variant] || "reveal";

  return (
    <Tag
      ref={ref}
      className={`${variantClass} ${played ? "reveal-play" : ""} ${className}`}
      style={{ ["--r-delay"]: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/** HeadlineMask — per-line text reveal. */
export const HeadlineMask = ({ children, delay = 0, className = "", tag: Tag = "span" }) => {
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;

    const reduceMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setPlayed(true);
      return;
    }

    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setPlayed(true);
    };

    const inView = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh && r.bottom > 0;
    };

    if (inView()) trigger();

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trigger();
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);

    const hardTimer = window.setTimeout(trigger, 1500);

    return () => {
      io.disconnect();
      window.clearTimeout(hardTimer);
    };
  }, [played]);

  return (
    <Tag
      ref={ref}
      className={`headline-mask ${played ? "reveal-play" : ""} ${className}`}
      style={{ ["--r-delay"]: `${delay}ms` }}
    >
      <span>{children}</span>
    </Tag>
  );
};

export default Reveal;
